import { useEffect, useState } from "react";
import {
  afterNextPaint,
  hideBootSplash,
  waitForIconFonts,
  waitForImages,
  waitForSiteConfig,
  waitForWindowLoad,
} from "../utils/appReady";

// How long each stage may hold the splash before we give up waiting and show
// the app anyway. A slow CDN should cost the user a moment, never the page.
const BOOT_TIMEOUT = 8000;
const IMAGE_TIMEOUT = 4000;
// Hard stop for the whole boot stage, in case one of the waits never settles.
const BOOT_HARD_TIMEOUT = 10000;

/**
 * Holds the app back until the page can be shown fully formed.
 *
 * "boot"     - nothing mounted yet; the shell's splash is on screen. We wait
 *              for window load, the site config (image base URL) and the icon
 *              fonts, so no screen ever mounts with broken images or with
 *              Material ligature names showing as raw text.
 * "painting" - the tree is mounted but still hidden behind the splash while
 *              its images finish loading.
 * "ready"    - splash fades out, page is on screen.
 */
const AppLoadGate = ({ children }) => {
  const [phase, setPhase] = useState("boot");

  useEffect(() => {
    if (phase !== "boot") return undefined;

    let cancelled = false;
    const paint = () => {
      if (!cancelled) setPhase("painting");
    };

    // Runs even if one of the waits below throws or never settles.
    const hardStop = setTimeout(paint, BOOT_HARD_TIMEOUT);

    Promise.all([
      waitForWindowLoad({ timeout: BOOT_TIMEOUT }),
      waitForSiteConfig({ timeout: BOOT_TIMEOUT }),
      waitForIconFonts({ timeout: BOOT_TIMEOUT }),
    ])
      .catch(() => {})
      .then(() => {
        clearTimeout(hardStop);
        paint();
      });

    return () => {
      cancelled = true;
      clearTimeout(hardStop);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "painting") return undefined;

    let cancelled = false;

    afterNextPaint()
      .then(() => waitForImages({ timeout: IMAGE_TIMEOUT }))
      .catch(() => {})
      .then(() => {
        if (!cancelled) setPhase("ready");
      });

    return () => {
      cancelled = true;
    };
  }, [phase]);

  useEffect(() => {
    if (phase === "ready") hideBootSplash();
  }, [phase]);

  // Nothing to render during "boot" - the shell's splash owns the screen until
  // the first screen is mounted and painted underneath it.
  return phase === "boot" ? null : children;
};

export default AppLoadGate;
